import { ShieldCheck, Eye, KeyRound, FileCheck2, Lock, RotateCw, Database, Shield } from "lucide-react";
import SEO from "@/components/SEO";
import CTABand from "@/components/CTABand";
import NistAlignment from "@/components/NistAlignment";
import TrustBoundaryDiagram from "@/components/TrustBoundaryDiagram";

const trustPoints = [
  {
    title: "Protection across every layer",
    body: "Protect users, devices, connections and infrastructure through one integrated security architecture.",
    icon: <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />,
  },
  {
    title: "Stop unauthorised access earlier",
    body: "Check identity, roles and routes at the gateway before requests reach protected services.",
    icon: <KeyRound className="h-5 w-5" strokeWidth={1.75} />,
  },
  {
    title: "Protect people and devices",
    body: "Validate identity, device posture and authorisation before a connection joins the network.",
    icon: <Eye className="h-5 w-5" strokeWidth={1.75} />,
  },
  {
    title: "Security you can demonstrate",
    body: "Clear controls, monitoring and review processes help teams understand and demonstrate their security posture.",
    icon: <FileCheck2 className="h-5 w-5" strokeWidth={1.75} />,
  },
  {
    title: "Quantum-resilient architecture",
    body: "A modular cryptographic architecture with a clear path toward post-quantum readiness.",
    icon: <Lock className="h-5 w-5" strokeWidth={1.75} />,
  },
  {
    title: "Automation with human control",
    body: "Routine work can be automated while important changes remain authorised by people.",
    icon: <RotateCw className="h-5 w-5" strokeWidth={1.75} />,
  },
];

export default function Security() {
  return (
    <>
      <SEO
        title="Security and Trust — LightningFlow"
        description="Understand LightningFlow trust boundaries, identity-aware controls, protected connectivity and security framework alignment."
        canonicalPath="/security"
      />

      <section className="lf-hero-editorial" data-testid="security-hero">
        <div className="lf-container grid gap-12 py-16 md:py-24 lg:grid-cols-[1.1fr_.9fr] lg:items-center lg:py-28">
          <div>
            <div className="lf-hero-kicker">Security &amp; Trust</div>
            <h1 className="lf-hero-title max-w-5xl">Security by <em>architecture.</em></h1>
            <p className="lf-hero-summary">
              Protect users, devices and infrastructure with security built into every connection—not bolted on afterwards. QuantumCloak reduces exposure, QuantumCubeDB connects security signals with context and Halo extends protection to people and devices.
            </p>
            <div className="lf-hero-actions">
              <a href="#trust-model" className="lf-btn-primary">Review trust model</a>
              <a href="/evidence" className="lf-btn-secondary">Open Trust Centre</a>
            </div>
          </div>
          <div className="rounded-sm border border-white/15 bg-black/20 p-4 md:p-5">
            <TrustBoundaryDiagram dark />
          </div>
        </div>
      </section>

      <section id="trust-model" className="scroll-mt-28 border-b border-ink-100 bg-white" data-testid="security-nist-alignment">
        <div className="lf-container py-10 md:py-12"><NistAlignment /></div>
      </section>

      <section className="lf-section border-b border-ink-100 bg-ink-50/60" data-testid="security-trust-boundary">
        <div className="lf-container">
          <div className="lf-eyebrow">Trust boundaries</div>
          <h2 className="mt-4 max-w-4xl font-display text-4xl font-black leading-[1.02] md:text-6xl">Verify the request before it reaches protected infrastructure.</h2>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-600">LightningFlow combines identity, device and policy context at a defined access boundary, then retains operational evidence for review and governed response.</p>
          <TrustBoundaryDiagram />
        </div>
      </section>

      {/* Trust points */}
      <section className="lf-section" data-testid="security-trust-points">
        <div className="lf-container">
          <div className="lf-eyebrow">Security advantages</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl">
            Protection you can see and control.
          </h2>
          <div className="lf-editorial-list">
            {trustPoints.map((t, i) => (
              <article
                key={t.title}
                data-testid={`security-point-${i + 1}`}
              >
                <span className="lf-list-index">0{i + 1}</span>
                <div className="flex items-start gap-4"><span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center border border-ink-200">{t.icon}</span><h3>{t.title}</h3></div>
                <p>{t.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* QuantumCloak */}
      <section
        className="lf-section bg-[#031124] text-white relative overflow-hidden"
        data-testid="security-quantumcloak"
      >
        <div className="absolute inset-0 lf-grid-bg-faint opacity-20 pointer-events-none" />
        <div className="absolute -right-32 -top-32 h-[480px] w-[480px] bg-[radial-gradient(closest-side,rgba(3,105,161,0.4),transparent_70%)] pointer-events-none" />
        <div className="lf-container relative grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="lf-eyebrow text-ink-300">QuantumCloak</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Reduce what attackers can see and reach.
            </h2>
            <div className="mt-6 inline-flex items-center justify-center h-12 w-12 bg-white/10 border border-white/15">
              <Shield className="h-6 w-6" strokeWidth={1.75} />
            </div>
          </div>
          <div className="md:col-span-7 space-y-5 text-ink-200 text-base md:text-lg leading-relaxed">
            <p>
              QuantumCloak reduces what attackers can see and reach. It protects connections between users, devices, sites and cloud services while giving operators clear visibility and control.
            </p>
            <p>
              Identity and policy are checked before access is granted, helping keep critical infrastructure private without slowing legitimate work.
            </p>
            <p className="text-sky-100/80">
              Because QuantumCloak is part of the LightningFlow platform, protection, network intelligence and response work together instead of operating as disconnected tools.
            </p>
            <div className="pt-2 flex flex-wrap gap-2">
              <span className="lf-pill bg-white/10 text-white border-white/20">Distributed protection</span>
              <span className="lf-pill bg-white/10 text-white border-white/20">Endpoint cloaking</span>
              <span className="lf-pill bg-white/10 text-white border-white/20">Post-quantum resilience</span>
              <span className="lf-pill bg-white/10 text-white border-white/20">Zero-trust-style operations</span>
            </div>
          </div>
        </div>
      </section>

      {/* QuantumCubeDB */}
      <section className="lf-section" data-testid="security-quantumcubedb">
        <div className="lf-container grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="lf-eyebrow">QuantumCubeDB</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Turn security signals into a clear picture.
            </h2>
            <div className="mt-6 inline-flex items-center justify-center h-12 w-12 bg-ink-900 text-white">
              <Database className="h-6 w-6" strokeWidth={1.75} />
            </div>
          </div>
          <div className="md:col-span-7 space-y-5 text-ink-600 text-base md:text-lg leading-relaxed">
            <p>
              QuantumCubeDB brings network health, events, policy and security signals into one real-time view, helping teams spot patterns, investigate incidents and make faster decisions.
            </p>
            <p>
              Built for connected operations, QuantumCubeDB brings together:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {[
                "Distributed data systems",
                "Graph-style relationships",
                 "Real-time network health",
                "Multi-dimensional correlation",
                 "Fast event and signal correlation",
                 "Context for intelligent operations",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 border-l border-ink-100 pl-4 py-1">
                  <span className="font-display text-base font-semibold text-ink-900">{item}</span>
                </li>
              ))}
            </ul>
            <p>
              Teams can move from an alert to the surrounding context without jumping between disconnected data sources.
            </p>
            <p className="text-ink-500">
              QuantumCubeDB gives LightningFlow and Sheila AI the connected context needed to explain what is happening and guide the next step.
            </p>
          </div>
        </div>
      </section>

      {/* Joint foundation */}
      <section className="bg-ink-900 text-white" data-testid="security-joint">
        <div className="lf-container py-20 md:py-24">
          <div className="lf-eyebrow text-ink-300">Together</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-4xl">
            Protection. Awareness. Intelligence.
          </h2>
          <p className="mt-6 max-w-3xl text-ink-200 leading-relaxed text-base md:text-lg">
            Together, QuantumCloak and QuantumCubeDB form the foundation of LightningFlow's security and intelligence architecture — combining protection, operational awareness, and distributed infrastructure intelligence into a unified ecosystem.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            <div className="bg-ink-900 p-8" data-testid="security-joint-cloak">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center bg-white/10 border border-white/15">
                  <Shield className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-300">QuantumCloak</div>
              </div>
              <p className="mt-5 font-display text-2xl font-bold tracking-tight">Security & protection layer</p>
            </div>
            <div className="bg-ink-900 p-8" data-testid="security-joint-cubedb">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center bg-white/10 border border-white/15">
                  <Database className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-300">QuantumCubeDB</div>
              </div>
              <p className="mt-5 font-display text-2xl font-bold tracking-tight">Data & intelligence layer</p>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
